import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Paper,
  Grid,
  Divider,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Sync as SyncIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

const ConflictResolutionDialog = ({
  conflict,
  open,
  onResolve,
  onClose,
  customFieldRenderer = null,
}) => {
  if (!conflict) return null;

  const handleResolve = (resolution, mergedData = null) => {
    onResolve(conflict.id, resolution, mergedData);
    onClose();
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined) return "Empty";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    if (typeof value === "boolean") return value ? "True" : "False";
    return String(value);
  };

  const renderFieldComparison = (field) => {
    const clientValue = conflict.clientData[field];
    const serverValue = conflict.serverData[field];

    if (customFieldRenderer) {
      const customRender = customFieldRenderer(field, clientValue, serverValue);
      if (customRender) return customRender;
    }

    return (
      <Paper key={field} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom color="primary">
          {field.replace(/_/g, " ").toUpperCase()}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <EditIcon sx={{ fontSize: 16, mr: 1 }} />
                Your Changes
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
              >
                {formatFieldValue(clientValue)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ p: 2, bgcolor: "warning.light", borderRadius: 1 }}>
              <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                <SyncIcon sx={{ fontSize: 16, mr: 1 }} />
                Server Update
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
              >
                {formatFieldValue(serverValue)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: "60vh" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningIcon color="warning" />
        <Box>
          <Typography variant="h6">Edit Conflict Detected</Typography>
          <Typography variant="body2" color="text.secondary">
            {conflict.entityType} "{conflict.entityId}" was updated while you
            were editing
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            The following fields have conflicts:
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            {conflict.conflictedFields.map((field) => (
              <Chip
                key={field}
                label={field.replace(/_/g, " ")}
                size="small"
                color="warning"
              />
            ))}
          </Box>
          <Divider sx={{ my: 2 }} />
        </Box>

        {conflict.conflictedFields.map(renderFieldComparison)}

        <Box sx={{ mt: 3, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
          <Typography variant="body2" color="info.dark">
            <strong>What happened?</strong> Someone else updated this{" "}
            {conflict.entityType}
            while you were editing. Choose how to resolve these conflicts:
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={() => handleResolve("keep_client")}
          variant="outlined"
          color="primary"
          startIcon={<EditIcon />}
        >
          Keep My Changes
        </Button>
        <Button
          onClick={() => handleResolve("accept_server")}
          variant="outlined"
          color="warning"
          startIcon={<SyncIcon />}
        >
          Accept Server Update
        </Button>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConflictResolutionDialog;
